<template>
    <BaseDropdown
        class="tags-dropdown"
        v-bind="$props"
        v-on="$listeners"
        toggleDisabled
        :triggerCloseMenu="triggerClose"
        @openMenu="openMenu"
        @closeMenu="closeMenu">
        <template slot="dropdownField">
            <div class="tags-dropdown-field"
                :class="{'focused': menuOpen && !isDisable, 'disabled': isDisable}"
                @click="focusInput">
                <!-- Selected values list -->
                <TagsDropdownValues
                    class="values"
                    :options="selectedOptions"
                    :idField="idField"
                    :displayField="selectedDisplayTextField"
                    :tooltipField="tooltipField"
                    @remove="deselectOption">
                    <!-- Search Input -->
                    <input
                        type="text"
                        class="search-input"
                        :ref="`search${uniqueId}`"
                        v-model="inputValue"
                        :placeholder="placeholder"
                        autocomplete="off"
                        spellcheck="false"
                        :style="{width: inputWidth + 'px'}"
                        :disabled="isDisable"
                        @keyup.esc="closeMenu"
                        @keyup.enter="handleEnter"
                        @keyup.down="navigateToNext"
                        @keyup.up="navigateToPrev"
                    />
                    <template v-slot:preOption="{ option }">
                        <slot name="preValue" :option="option"></slot>
                    </template>
                </TagsDropdownValues>
            </div>
        </template>
        <!-- Dropdown -->
        <template slot="dropdownArea">
            <!-- Dropdown options list -->
            <LeavesDropdown
                class="options"
                :style="{'max-height': maxListHeight + 'px'}"
                :options="filteredOptions"
                :idField="idField"
                :displayField="displayField"
                :activeIndex="activeIndex"
                :lazyRender="lazyRender"
                @dropdownSelectedOption="selectOption">
                <template #preOptionInner="{ option }">
                    <slot name="preOptionInner" :option="option"/>
                </template>
            </LeavesDropdown>
            <div class="empty-text empty-result" v-if="inputValue && !filteredOptions.length">
                <span class="font-4">No items matched your search</span>
            </div>
            <div class="empty-text empty-options" v-else-if="!filteredOptions.length">
                <span class="font-4">No items</span>
            </div>
        </template>
    </BaseDropdown>
</template>

<script>
import BaseDropdown from 'components/dropdowns/BaseDropdown.vue';
import LeavesDropdown from 'components/dropdowns/LeavesDropdown.vue';
import { isEmpty } from 'lodash';
import TagsDropdownValues from './TagsDropdownValues.vue';

export default {
    name: 'TagsDropdown',
    components: { LeavesDropdown, BaseDropdown, TagsDropdownValues },
    props: {
        ...BaseDropdown.props,
        height: {
            type: [Number, String],
            default: 'auto'
        },
        selectedIds: {
            type: Array,
            default: () => []
        },
        tooltipField: String,
        placeholder: {
            type: String,
            default: 'Add Item'
        },
        searchFields: {
            type: Array,
            default: () => []
        },
        lazyRender: Boolean,
        allowCustomInput: Boolean,
        customInputRegExp: RegExp
    },
    data() {
        return {
            activeIndex: null,
            menuOpen: false,
            inputValue: '',
            triggerClose: false
        };
    },
    computed: {
        componentId() {
            return `ssaTagsDropdownId${this.uniqueId}`;
        },
        cleanInputValue() {
            return this.inputValue.trim();
        },
        inputWidth() {
            const approxCharWidth = 8.3;
            const minInputWidth = 120;
            return Math.max(this.inputValue.length * approxCharWidth, minInputWidth);
        },
        dropdownOptions() {
            // exclude already selected options
            return this.options.filter((option) => {
                const isSelected = this.selectedIds.includes(option[this.idField]);
                return !isSelected || option.isHeader;
            });
        },
        selectedOptions() {
            return this.selectedIds.map((id) => {
                const foundOption = this.options
                    .filter((o) => !o.isHeader) // exclude groups, they can't be selected
                    .find((o) => o[this.idField] === id);
                return foundOption || this.makeCustomOption(id);
            });
        },
        searchableFields() {
            return this.searchFields.length ? this.searchFields : [this.displayField];
        },
        filteredOptions() {
            const searchTerm = this.cleanInputValue.toLowerCase();
            // filter options by search fields
            const options = this.dropdownOptions.filter((option) => {
                // ignore if search not set, or option is group
                if (!searchTerm || option.isHeader) return true;
                // match option based on search fields
                return this.searchableFields.some((field) => {
                    const fieldValue = option[field] || '';
                    return fieldValue.toLowerCase().indexOf(searchTerm) > -1;
                });
            });
            // filter out empty groups
            return options.filter((option, index) => {
                const nextOption = options[index + 1];
                return option.isHeader ? nextOption && !nextOption.isHeader : true;
            });
        },
        activeOption() {
            if (this.activeIndex === null || !this.filteredOptions.length) return null;
            return this.filteredOptions[this.activeIndex];
        }
    },
    methods: {
        openMenu() {
            this.resetActiveIndex();
            this.menuOpen = true;
            this.triggerClose = false;
        },
        closeMenu() {
            this.menuOpen = false;
            this.triggerClose = true;
        },
        focusInput() {
            if (this.isDisable) return;
            this.$refs[`search${this.uniqueId}`].focus();
        },
        selectOption(option) {
            if (isEmpty(option) || option.isHeader || this.isDisable) return;
            this.inputValue = '';
            this.resetActiveIndex();
            this.focusInput();
            this.$emit('itemSelected', option);
        },
        deselectOption(option) {
            if (this.isDisable) return;
            if (this.menuOpen) this.focusInput();
            this.$emit('itemDeselected', option);
        },
        selectCustomOption() {
            if (!this.allowCustomInput || !this.cleanInputValue) return;
            // add if option with such id is not already selected
            if (this.selectedIds.indexOf(this.cleanInputValue) === -1) {
                this.selectOption(this.makeCustomOption(this.cleanInputValue));
            }
        },
        makeCustomOption(value) {
            const isInvalid = this.customInputRegExp && !this.customInputRegExp.test(value);
            return {
                [this.idField]: value,
                [this.displayField]: value,
                isCustom: true,
                ...(isInvalid ? { isInvalid } : {})
            };
        },
        resetActiveIndex() {
            this.activeIndex = null;
        },
        handleEnter() {
            if (this.activeOption) {
                this.selectOption(this.activeOption);
            } else {
                this.selectCustomOption();
            }
        },
        navigateToNext() {
            const optionsCount = this.filteredOptions.length;
            if (!optionsCount) return;
            // navigate to next option
            if (this.activeIndex === null) {
                this.activeIndex = 0;
            } else if (this.activeIndex < optionsCount - 1) {
                this.activeIndex += 1;
            }
            // skip groups when navigating options
            if (this.activeOption && this.activeOption.isHeader) {
                this.navigateToNext();
            }
        },
        navigateToPrev() {
            if (this.activeIndex > 0) {
                this.activeIndex -= 1;
            } else {
                this.resetActiveIndex();
            }
            // skip groups when navigating options
            if (this.activeOption && this.activeOption.isHeader) {
                this.navigateToPrev();
            }
        }
    },
    watch: {
        inputValue() {
            this.resetActiveIndex();
        }
    }
};
</script>

<style lang="scss" scoped>
@import "~styles/styleGuide.scss";
@import "~styles/input.scss";

.tags-dropdown {
    height: auto;
    position: relative;
    min-width: 270px;
    width: 100%;
}

.tags-dropdown-field {
    border: $border;
    background: $white;
    border-radius: $borderRadius;
    &.focused {
        border-color: $primary;
    }
    &.disabled {
        opacity: 0.7;
    }
}

input.search-input {
    max-width: 100%;
    border: none;
    height: 30px;
    line-height: 30px;
    position: relative;
    top: -3px;
    &:focus {
        outline: none;
        border: none;
    }
    &[disabled] {
        background-color: transparent;
    }
}

.empty-text {
    background: $white;
    color: $light-800;
    padding: 12px 20px 11px;
    white-space: nowrap;
    cursor: default;
}

// dropdown area styles
::v-deep .dropdown-area {
    box-shadow: 0 2px 4px 0 rgba(0,0,0,.3);
    .preset-element {
        padding-left: 25px;
    }
}
</style>
